/**
 * 将图片URL转换为Base64格式
 * 如果转换失败或超时，返回备用URL
 * 
 * @param primaryUrl 首选图片URL
 * @param fallbackUrl 备用图片URL
 * @param outputFormat 输出的Base64格式，默认png
 * @returns Promise<string> 成功返回Base64字符串，失败返回备用URL
 */
export async function getValidImageBase64(
  primaryUrl: string,
  fallbackUrl: string,
  outputFormat: 'png' | 'jpeg' | 'webp' = 'png'
): Promise<string> {
  return new Promise((resolve) => {
    // 边界值处理：如果首选URL为空，直接返回备用URL
    if (!primaryUrl) {
      resolve(fallbackUrl);
      return;
    }

    const img = new Image();
    // 解决跨域图片转换Base64时的画布污染问题
    img.crossOrigin = 'anonymous';

    // 设置超时时间（可选，防止长时间等待）
    const timeoutId = setTimeout(() => {
      img.onerror = null; // 清除事件监听，避免重复触发
      resolve(fallbackUrl);
    }, 5000); // 5秒超时

    // 图片加载成功 - 转换为Base64
    img.onload = () => {
      try {
        clearTimeout(timeoutId); // 清除超时定时器
        
        // 创建canvas用于转换图片
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // 确保获取到2D上下文
        if (!ctx) {
          resolve(fallbackUrl);
          return;
        }

        // 设置canvas尺寸与图片一致
        canvas.width = img.width;
        canvas.height = img.height;

        // 绘制图片到canvas
        ctx.drawImage(img, 0, 0);

        // 将canvas内容转换为Base64字符串
        const quality = outputFormat === 'jpeg' || outputFormat === 'webp' ? 0.95 : 1.0;
        const base64Str = canvas.toDataURL(`image/${outputFormat}`, quality);
        
        // 返回Base64字符串（可直接用于img的src）
        resolve(base64Str);
      } catch (error) {
        // 转换Base64失败时返回备用URL
        console.warn('图片转换Base64失败:', error);
        resolve(fallbackUrl);
      }
    };

    // 图片加载失败
    img.onerror = () => {
      clearTimeout(timeoutId); // 清除超时定时器
      resolve(fallbackUrl);
    };

    // 开始加载图片
    img.src = primaryUrl;
  });
}
